// src/modules/leads/route-planner/routePlannerStyles.js
import styled, { keyframes, css, createGlobalStyle } from 'styled-components';

// ─── Animations ──────────────────────────────────────────────────────────────

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(8px); }
  to { opacity: 1; transform: translateY(0); }
`;

const pulse = keyframes`
  0%, 100% { transform: scale(1); opacity: 0.8; }
  50% { transform: scale(1.15); opacity: 1; }
`;

const slideUp = keyframes`
  from { transform: translateY(100%); }
  to { transform: translateY(0); }
`;

// ─── Global: Google autocomplete dropdown on mobile ─────────────────────────

export const PacContainerStyles = createGlobalStyle`
  @media (max-width: 768px) {
    .pac-container {
      z-index: 10001 !important;
      border-radius: 0 0 12px 12px;
      box-shadow: 0 4px 16px rgba(0,0,0,0.3);
      border: none;
      background: ${p => p.theme.colors.surface};
    }
    .pac-item {
      padding: 12px 16px;
      font-size: 14px;
      line-height: 1.4;
      border-top: 1px solid ${p => p.theme.colors.inputGray}22;
      color: ${p => p.theme.colors.textPrimary};
      cursor: pointer;
    }
    .pac-item:hover {
      background: ${p => p.theme.colors.primary}11;
    }
    .pac-item-query {
      color: ${p => p.theme.colors.textPrimary};
      font-size: 14px;
    }
    .pac-matched {
      font-weight: 600;
    }
    .pac-icon { display: none; }
  }
`;

// ─── Layout ──────────────────────────────────────────────────────────────────

export const PageWrapper = styled.div`
  display: flex;
  flex-direction: column;
  height: calc(100vh - 130px);
  overflow: hidden;

  @media (max-width: 768px) {
    /* Break out of DashboardLayout MainContent padding */
    margin: -24px;
    margin-top: 0;
    width: calc(100% + 48px);
    height: calc(100dvh - 60px);
    position: relative;
  }
`;

export const PlannerContainer = styled.div`
  display: flex;
  flex: 1;
  overflow: hidden;
  border-radius: ${p => p.theme.borderRadius};
  background: ${p => p.theme.colors.surface};

  @media (max-width: 768px) {
    flex-direction: column;
    border-radius: 0;
    position: relative;
  }
`;

export const PlannerSidebar = styled.div`
  width: 400px;
  min-width: 400px;
  display: flex;
  flex-direction: column;
  background: ${p => p.theme.colors.surface};
  border-right: 1px solid ${p => p.theme.colors.inputGray}33;
  overflow: hidden;

  @media (max-width: 768px) {
    display: none;
  }
`;

export const PlannerMapContainer = styled.div`
  flex: 1;
  position: relative;
  min-height: 400px;

  @media (max-width: 768px) {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    min-height: unset;
  }
`;

// ─── Step Indicator ──────────────────────────────────────────────────────────

export const StepBarContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${p => p.theme.spacing(1.5)} ${p => p.theme.spacing(2)};
  background: ${p => p.theme.colors.surface};
  border-bottom: 1px solid ${p => p.theme.colors.inputGray}33;
  gap: 0;
  flex-shrink: 0;

  @media (max-width: 768px) {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    z-index: 10;
    background: ${p => p.theme.colors.surface}DD;
    backdrop-filter: blur(12px);
    -webkit-backdrop-filter: blur(12px);
    border-bottom: none;
    border-radius: 0 0 14px 14px;
    padding: 10px 16px;
    box-shadow: 0 2px 12px rgba(0,0,0,0.25);
  }
`;

export const StepCircle = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.75rem;
  font-weight: 600;
  flex-shrink: 0;
  transition: all 0.3s ease;

  ${p => p.status === 'completed' && css`
    background: ${p.theme.colors.success};
    color: #fff;
  `}
  ${p => p.status === 'active' && css`
    background: ${p.theme.colors.primary};
    color: #fff;
    box-shadow: 0 0 0 3px ${p.theme.colors.primary}33;
  `}
  ${p => p.status === 'pending' && css`
    background: transparent;
    border: 2px solid ${p.theme.colors.inputGray};
    color: ${p.theme.colors.inputGray};
  `}

  @media (max-width: 768px) {
    width: 26px;
    height: 26px;
    font-size: 0.6rem;
    svg { font-size: 0.55rem; }
  }
`;

export const StepLine = styled.div`
  flex: 1;
  height: 2px;
  max-width: 60px;
  margin: 0 4px;
  transition: background 0.3s ease;
  background: ${p => p.completed ? p.theme.colors.success : p.theme.colors.inputGray + '44'};

  @media (max-width: 768px) {
    max-width: 24px;
    margin: 0 3px;
  }
`;

export const StepLabel = styled.span`
  font-size: 0.7rem;
  color: ${p => p.active ? p.theme.colors.primary : p.theme.colors.textSecondary};
  margin-top: 4px;
  white-space: nowrap;
  transition: color 0.3s ease;

  @media (max-width: 768px) {
    display: none;
  }
`;

export const StepItem = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
`;

// ─── Sidebar Sections ────────────────────────────────────────────────────────

export const SidebarHeader = styled.div`
  padding: ${p => p.theme.spacing(2)};
  border-bottom: 1px solid ${p => p.theme.colors.inputGray}33;
  flex-shrink: 0;
`;

export const SidebarScrollArea = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: ${p => p.theme.spacing(1)};
  -webkit-overflow-scrolling: touch;

  &::-webkit-scrollbar { width: 6px; }
  &::-webkit-scrollbar-track { background: transparent; }
  &::-webkit-scrollbar-thumb {
    background: ${p => p.theme.colors.inputGray}44;
    border-radius: 3px;
  }
`;

export const SectionTitle = styled.h3`
  font-size: 0.85rem;
  font-weight: 600;
  color: ${p => p.theme.colors.textPrimary};
  margin: 0 0 ${p => p.theme.spacing(1)} 0;
  display: flex;
  align-items: center;
  gap: 8px;

  svg { font-size: 0.8rem; color: ${p => p.theme.colors.primary}; }

  @media (max-width: 768px) {
    font-size: 0.85rem;
    margin: 0 0 8px 0;
  }
`;

export const SectionSubtitle = styled.p`
  font-size: 0.8rem;
  color: ${p => p.theme.colors.textSecondary};
  margin: 0 0 ${p => p.theme.spacing(1.5)} 0;

  @media (max-width: 768px) {
    font-size: 0.8rem;
    margin: 0 0 10px 0;
  }
`;

// ─── Search & Filters ────────────────────────────────────────────────────────

export const SearchBox = styled.div`
  display: flex;
  align-items: center;
  background: ${p => p.theme.colors.background};
  border: 1px solid ${p => p.theme.colors.inputGray};
  border-radius: ${p => p.theme.borderRadius};
  padding: 0 ${p => p.theme.spacing(1)};
  margin-bottom: ${p => p.theme.spacing(1.5)};

  input {
    flex: 1;
    padding: ${p => p.theme.spacing(1)};
    background: transparent;
    border: none;
    color: ${p => p.theme.colors.textPrimary};
    font-size: 0.85rem;
    &:focus { outline: none; }
    &::placeholder { color: ${p => p.theme.colors.inputGray}; }
  }

  svg { color: ${p => p.theme.colors.textSecondary}; font-size: 0.8rem; }

  @media (max-width: 768px) {
    margin-bottom: 10px;
    border-radius: 12px;
    min-height: 44px;
    input {
      padding: 12px 8px;
      font-size: 16px;
    }
  }
`;

export const FilterRow = styled.div`
  display: flex;
  gap: ${p => p.theme.spacing(1)};
  align-items: center;
  flex-wrap: wrap;
  margin-bottom: ${p => p.theme.spacing(1)};

  @media (max-width: 768px) {
    flex-wrap: nowrap;
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
    gap: 8px;
    margin-bottom: 8px;
    padding-bottom: 2px;
    scrollbar-width: none;
    &::-webkit-scrollbar { display: none; }
  }
`;

export const FilterSelect = styled.select`
  padding: 6px 8px;
  border-radius: 4px;
  background: ${p => p.theme.colors.background};
  color: ${p => p.theme.colors.textPrimary};
  border: 1px solid ${p => p.theme.colors.inputGray};
  font-size: 0.8rem;
  flex: 1;
  min-width: 0;

  option { background: ${p => p.theme.colors.background}; }

  @media (max-width: 768px) {
    flex: 0 0 auto;
    min-width: 120px;
    padding: 10px 12px;
    font-size: 0.85rem;
    min-height: 40px;
    border-radius: 10px;
  }
`;

// ─── Buttons ─────────────────────────────────────────────────────────────────

export const PrimaryButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 10px 20px;
  border-radius: ${p => p.theme.borderRadius};
  background: ${p => p.theme.colors.primary};
  color: #fff;
  border: none;
  cursor: pointer;
  font-size: 0.85rem;
  font-weight: 600;
  width: 100%;
  transition: all 0.2s ease;

  &:hover { opacity: 0.9; transform: translateY(-1px); }
  &:active { transform: scale(0.98); opacity: 0.95; }
  &:disabled { opacity: 0.4; cursor: not-allowed; transform: none; }

  svg { font-size: 1rem; }

  @media (max-width: 768px) {
    padding: 14px 20px;
    font-size: 0.95rem;
    min-height: 48px;
    border-radius: 12px;
    gap: 8px;
    svg { font-size: 1rem; }
  }
`;

export const SecondaryButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 8px 16px;
  border-radius: ${p => p.theme.borderRadius};
  background: ${p => p.theme.colors.background};
  color: ${p => p.theme.colors.textSecondary};
  border: 1px solid ${p => p.theme.colors.inputGray};
  cursor: pointer;
  font-size: 0.8rem;
  transition: all 0.2s ease;

  &:hover { background: ${p => p.theme.colors.primary}22; color: ${p => p.theme.colors.primary}; }
  &:active { transform: scale(0.98); }
  &:disabled { opacity: 0.4; cursor: not-allowed; }

  @media (max-width: 768px) {
    padding: 12px 16px;
    font-size: 0.85rem;
    min-height: 44px;
    border-radius: 12px;
  }
`;

export const ActionButton = styled.button`
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 6px 10px;
  border-radius: 4px;
  background: ${p => {
    if (p.variant === 'danger') return '#e74c3c22';
    if (p.variant === 'success') return p.theme.colors.success + '22';
    if (p.variant === 'primary') return p.theme.colors.primary + '22';
    return p.theme.colors.background;
  }};
  color: ${p => {
    if (p.variant === 'danger') return '#e74c3c';
    if (p.variant === 'success') return p.theme.colors.success;
    if (p.variant === 'primary') return p.theme.colors.primary;
    return p.theme.colors.textSecondary;
  }};
  border: 1px solid ${p => {
    if (p.variant === 'danger') return '#e74c3c44';
    if (p.variant === 'success') return p.theme.colors.success + '44';
    if (p.variant === 'primary') return p.theme.colors.primary + '44';
    return p.theme.colors.inputGray;
  }};
  cursor: pointer;
  font-size: 0.75rem;
  white-space: nowrap;
  transition: all 0.2s ease;

  &:hover { opacity: 0.8; }
  &:active { transform: scale(0.95); }
  &:disabled { opacity: 0.4; cursor: not-allowed; }

  @media (max-width: 768px) {
    padding: 8px 14px;
    font-size: 0.8rem;
    min-height: 36px;
    border-radius: 8px;
  }
`;

export const ChipButton = styled.button`
  padding: 6px 14px;
  border-radius: 20px;
  font-size: 0.75rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  border: 1px solid ${p => p.active ? p.theme.colors.primary : p.theme.colors.inputGray};
  background: ${p => p.active ? p.theme.colors.primary + '22' : 'transparent'};
  color: ${p => p.active ? p.theme.colors.primary : p.theme.colors.textSecondary};

  &:hover { border-color: ${p => p.theme.colors.primary}; }
  &:active { transform: scale(0.96); }

  @media (max-width: 768px) {
    padding: 8px 14px;
    font-size: 0.8rem;
  }
`;

// ─── Cards ───────────────────────────────────────────────────────────────────

export const LeadCard = styled.div`
  padding: ${p => p.theme.spacing(1.5)};
  margin-bottom: ${p => p.theme.spacing(1)};
  background: ${p => p.isHighlighted ? p.theme.colors.primary + '11' : p.theme.colors.background};
  border: 1px solid ${p => p.isHighlighted ? p.theme.colors.primary + '44' : 'transparent'};
  border-radius: ${p => p.theme.borderRadius};
  cursor: pointer;
  transition: all 0.2s ease;
  border-left: 3px solid ${p => p.statusColor || '#95a5a6'};
  animation: ${fadeIn} 0.2s ease;

  &:hover {
    background: ${p => p.theme.colors.primary}11;
    transform: translateX(2px);
  }
  &:active { transform: scale(0.99); }

  @media (max-width: 768px) {
    padding: 14px;
    margin-bottom: 8px;
    border-radius: 12px;
    border-left-width: 4px;
  }
`;

export const StopCard = styled.div`
  display: flex;
  align-items: flex-start;
  gap: ${p => p.theme.spacing(1.5)};
  padding: ${p => p.theme.spacing(1.5)};
  margin-bottom: ${p => p.theme.spacing(1)};
  background: ${p => p.theme.colors.background};
  border-radius: ${p => p.theme.borderRadius};
  animation: ${fadeIn} 0.3s ease;

  @media (max-width: 768px) {
    gap: 12px;
    padding: 14px;
    margin-bottom: 8px;
    border-radius: 12px;
  }
`;

export const StopNumber = styled.div`
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: ${p => p.color || p.theme.colors.primary};
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.75rem;
  font-weight: 700;
  flex-shrink: 0;

  @media (max-width: 768px) {
    width: 28px;
    height: 28px;
    font-size: 0.7rem;
  }
`;

export const StopInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

export const StopName = styled.div`
  font-weight: 600;
  font-size: 0.85rem;
  color: ${p => p.theme.colors.textPrimary};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  @media (max-width: 768px) {
    font-size: 0.9rem;
  }
`;

export const StopAddress = styled.div`
  font-size: 0.75rem;
  color: ${p => p.theme.colors.textSecondary};
  margin-top: 2px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  @media (max-width: 768px) {
    font-size: 0.78rem;
  }
`;

export const StopMeta = styled.div`
  font-size: 0.7rem;
  color: ${p => p.theme.colors.primary};
  margin-top: 4px;

  @media (max-width: 768px) {
    font-size: 0.72rem;
  }
`;

export const CardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 4px;
  gap: 8px;
`;

export const CardName = styled.div`
  font-weight: 600;
  font-size: 0.9rem;
  color: ${p => p.theme.colors.textPrimary};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0;

  @media (max-width: 768px) {
    font-size: 0.9rem;
  }
`;

export const CardStatus = styled.span`
  display: inline-block;
  padding: 2px 8px;
  border-radius: 10px;
  font-size: 0.7rem;
  background-color: ${p => p.color}22;
  color: ${p => p.color};
  white-space: nowrap;
  flex-shrink: 0;

  @media (max-width: 768px) {
    padding: 2px 8px;
    font-size: 0.65rem;
  }
`;

export const CardInfo = styled.div`
  font-size: 0.8rem;
  color: ${p => p.theme.colors.textSecondary};
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 2px;

  svg { font-size: 0.7rem; flex-shrink: 0; }

  @media (max-width: 768px) {
    font-size: 0.8rem;
  }
`;

export const CardActions = styled.div`
  display: flex;
  gap: ${p => p.theme.spacing(1)};
  margin-top: ${p => p.theme.spacing(1)};

  @media (max-width: 768px) {
    margin-top: 8px;
  }
`;

// ─── Route Summary ───────────────────────────────────────────────────────────

export const RouteSummaryBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-around;
  padding: ${p => p.theme.spacing(1.5)};
  background: ${p => p.theme.colors.primary}11;
  border: 1px solid ${p => p.theme.colors.primary}33;
  border-radius: ${p => p.theme.borderRadius};
  margin-bottom: ${p => p.theme.spacing(1.5)};

  @media (max-width: 768px) {
    padding: 14px 12px;
    margin-bottom: 12px;
    border-radius: 12px;
  }
`;

export const RouteSummaryStat = styled.div`
  text-align: center;

  .value {
    font-size: 1.1rem;
    font-weight: 700;
    color: ${p => p.theme.colors.primary};
  }
  .label {
    font-size: 0.65rem;
    color: ${p => p.theme.colors.textSecondary};
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }

  @media (max-width: 768px) {
    .value { font-size: 1.2rem; }
    .label { font-size: 0.65rem; letter-spacing: 1px; }
  }
`;

// ─── Along Route ─────────────────────────────────────────────────────────────

export const DistanceSelector = styled.div`
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
  margin-bottom: ${p => p.theme.spacing(1.5)};

  @media (max-width: 768px) {
    gap: 6px;
    margin-bottom: 10px;
  }
`;

export const AlongRouteResults = styled.div`
  margin-top: ${p => p.theme.spacing(1)};
`;

// ─── Share Panel ─────────────────────────────────────────────────────────────

export const ShareUrlBox = styled.div`
  display: flex;
  align-items: center;
  background: ${p => p.theme.colors.background};
  border: 1px solid ${p => p.theme.colors.inputGray};
  border-radius: ${p => p.theme.borderRadius};
  padding: ${p => p.theme.spacing(1)};
  margin-bottom: ${p => p.theme.spacing(1.5)};

  input {
    flex: 1;
    background: transparent;
    border: none;
    color: ${p => p.theme.colors.textSecondary};
    font-size: 0.75rem;
    min-width: 0;
    &:focus { outline: none; }
  }

  @media (max-width: 768px) {
    padding: 10px;
    margin-bottom: 12px;
    border-radius: 12px;
    input { font-size: 0.78rem; }
  }
`;

export const CopyButton = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 14px;
  border-radius: 4px;
  background: ${p => p.copied ? p.theme.colors.success + '22' : p.theme.colors.primary + '22'};
  color: ${p => p.copied ? p.theme.colors.success : p.theme.colors.primary};
  border: 1px solid ${p => p.copied ? p.theme.colors.success + '44' : p.theme.colors.primary + '44'};
  cursor: pointer;
  font-size: 0.75rem;
  font-weight: 500;
  white-space: nowrap;
  transition: all 0.2s ease;
  flex-shrink: 0;

  &:hover { opacity: 0.8; }
  &:active { transform: scale(0.95); }

  @media (max-width: 768px) {
    padding: 8px 14px;
    font-size: 0.78rem;
    border-radius: 8px;
  }
`;

// ─── Mobile Bottom Sheet ─────────────────────────────────────────────────────

export const BottomSheetOverlay = styled.div`
  display: none;

  @media (max-width: 768px) {
    position: fixed;
    top: 0; left: 0; right: 0; bottom: 0;
    background: rgba(0, 0, 0, 0.4);
    z-index: 999;
    opacity: ${p => p.expanded ? 1 : 0};
    pointer-events: ${p => p.expanded ? 'auto' : 'none'};
    transition: opacity 0.3s ease;
    display: block;
  }
`;

export const BottomSheet = styled.div`
  display: none;

  @media (max-width: 768px) {
    display: flex;
    flex-direction: column;
    position: fixed;
    left: 0;
    right: 0;
    bottom: 56px; /* Sits above MobileNavigation */
    z-index: 1001;
    background: ${p => p.theme.colors.surface};
    border-radius: 16px 16px 0 0;
    box-shadow: 0 -4px 24px rgba(0, 0, 0, 0.5);
    transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    max-height: calc(100dvh - 60px - 56px); /* Between header and nav */
    will-change: transform;

    ${p => p.sheetState === 'collapsed' && css`
      transform: translateY(calc(100% - 64px));
    `}
    ${p => p.sheetState === 'half' && css`
      transform: translateY(50%);
    `}
    ${p => p.sheetState === 'expanded' && css`
      transform: translateY(0);
    `}
  }
`;

export const DragHandle = styled.div`
  display: flex;
  justify-content: center;
  padding: 12px 0 8px;
  cursor: grab;
  flex-shrink: 0;
  -webkit-tap-highlight-color: transparent;
  min-height: 44px;
  align-items: flex-start;

  &::before {
    content: '';
    width: 40px;
    height: 5px;
    border-radius: 3px;
    background: ${p => p.theme.colors.inputGray};
  }

  &:active { cursor: grabbing; }
`;

export const SheetSummary = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 16px 10px;
  flex-shrink: 0;
`;

export const SheetSummaryText = styled.span`
  font-size: 0.95rem;
  font-weight: 600;
  color: ${p => p.theme.colors.textPrimary};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0;
`;

export const SheetContent = styled.div`
  flex: 1;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  overscroll-behavior: contain;
  padding: 0 16px 16px;
  padding-bottom: calc(32px + env(safe-area-inset-bottom, 0px));

  &::-webkit-scrollbar { width: 3px; }
  &::-webkit-scrollbar-thumb {
    background: ${p => p.theme.colors.inputGray}44;
    border-radius: 2px;
  }
`;

// ─── Geolocation Prompt ──────────────────────────────────────────────────────

export const PromptCard = styled.div`
  padding: ${p => p.theme.spacing(3)};
  text-align: center;
  animation: ${fadeIn} 0.3s ease;

  svg.prompt-icon {
    font-size: 2.5rem;
    color: ${p => p.theme.colors.primary};
    margin-bottom: ${p => p.theme.spacing(2)};
  }

  h3 {
    font-size: 1rem;
    color: ${p => p.theme.colors.textPrimary};
    margin: 0 0 ${p => p.theme.spacing(1)} 0;
  }

  p {
    font-size: 0.85rem;
    color: ${p => p.theme.colors.textSecondary};
    margin: 0 0 ${p => p.theme.spacing(2)} 0;
  }

  @media (max-width: 768px) {
    padding: 16px 8px;

    svg.prompt-icon {
      font-size: 2.2rem;
      margin-bottom: 12px;
    }
    h3 {
      font-size: 1.1rem;
      margin-bottom: 8px;
    }
    p {
      font-size: 0.88rem;
      margin-bottom: 12px;
    }
  }
`;

// ─── Mobile-only helper components ──────────────────────────────────────────

export const OrDivider = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin: 10px 0 0;

  &::before, &::after {
    content: '';
    flex: 1;
    height: 1px;
    background: ${p => p.theme.colors.inputGray}44;
  }

  span {
    font-size: 0.72rem;
    color: ${p => p.theme.colors.inputGray};
    flex-shrink: 0;
  }
`;

export const ButtonRow = styled.div`
  display: flex;
  gap: 8px;
  margin-bottom: ${p => p.mb || '10px'};
`;

export const ErrorText = styled.div`
  color: ${p => p.theme.colors.error};
  font-size: 0.78rem;
  margin-bottom: 8px;
`;

// ─── Empty / Loading States ──────────────────────────────────────────────────

export const EmptyState = styled.div`
  padding: ${p => p.theme.spacing(4)} ${p => p.theme.spacing(2)};
  text-align: center;
  color: ${p => p.theme.colors.textSecondary};
  font-size: 0.85rem;

  @media (max-width: 768px) {
    padding: 24px 12px;
    font-size: 0.85rem;
  }
`;

export const LoadingOverlay = styled.div`
  position: absolute;
  top: 0; left: 0; right: 0; bottom: 0;
  background: rgba(0, 0, 0, 0.3);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 100;
  border-radius: ${p => p.theme.borderRadius};
`;

const spin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

export const Spinner = styled.div`
  width: 40px;
  height: 40px;
  border: 3px solid ${p => p.theme.colors.inputGray}44;
  border-top-color: ${p => p.theme.colors.primary};
  border-radius: 50%;
  animation: ${spin} 0.8s linear infinite;
`;

// ─── Lead Count Bar ──────────────────────────────────────────────────────────

export const LeadCountBar = styled.div`
  padding: ${p => p.theme.spacing(1)} ${p => p.theme.spacing(2)};
  font-size: 0.8rem;
  color: ${p => p.theme.colors.textSecondary};
  border-bottom: 1px solid ${p => p.theme.colors.inputGray}33;
  flex-shrink: 0;

  @media (max-width: 768px) {
    padding: 8px 0;
    font-size: 0.78rem;
    border: none;
  }
`;

// ─── Divider ─────────────────────────────────────────────────────────────────

export const Divider = styled.div`
  height: 1px;
  background: ${p => p.theme.colors.inputGray}33;
  margin: ${p => p.theme.spacing(2)} 0;

  @media (max-width: 768px) {
    margin: 12px 0;
  }
`;

// ─── Onboarding Overlay ─────────────────────────────────────────────────────

const shimmer = keyframes`
  0% { background-position: -200% 0; }
  100% { background-position: 200% 0; }
`;

const float = keyframes`
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-8px); }
`;

const scaleIn = keyframes`
  from { opacity: 0; transform: scale(0.9); }
  to { opacity: 1; transform: scale(1); }
`;

const checkPop = keyframes`
  0% { transform: scale(0); opacity: 0; }
  60% { transform: scale(1.2); }
  100% { transform: scale(1); opacity: 1; }
`;

export const OnboardingOverlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${p => p.theme.colors.background};
  padding: 24px;

  @media (max-width: 768px) {
    padding: 24px 24px;
    align-items: center;
    justify-content: center;
  }
`;

export const OnboardingCard = styled.div`
  width: 100%;
  max-width: 400px;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  animation: ${scaleIn} 0.4s cubic-bezier(0.16, 1, 0.3, 1);
`;

export const OnboardingIconRing = styled.div`
  width: 96px;
  height: 96px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 28px;
  background: ${p => p.color || p.theme.colors.primary}18;
  border: 2px solid ${p => p.color || p.theme.colors.primary}33;
  animation: ${float} 3s ease-in-out infinite;

  svg {
    font-size: 2.4rem;
    color: ${p => p.color || p.theme.colors.primary};
  }

  @media (max-width: 768px) {
    width: 80px;
    height: 80px;
    margin-bottom: 24px;
    svg { font-size: 2rem; }
  }
`;

export const OnboardingTitle = styled.h2`
  font-size: 1.4rem;
  font-weight: 700;
  color: ${p => p.theme.colors.textPrimary};
  margin: 0 0 10px 0;
  letter-spacing: -0.3px;

  @media (max-width: 768px) {
    font-size: 1.5rem;
    margin-bottom: 10px;
  }
`;

export const OnboardingDesc = styled.p`
  font-size: 0.9rem;
  line-height: 1.5;
  color: ${p => p.theme.colors.textSecondary};
  margin: 0 0 32px 0;
  max-width: 320px;

  @media (max-width: 768px) {
    font-size: 0.95rem;
    margin-bottom: 28px;
    max-width: 320px;
    line-height: 1.6;
  }
`;

export const OnboardingButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  width: 100%;
  max-width: 300px;
  padding: 14px 24px;
  border-radius: 14px;
  border: none;
  cursor: pointer;
  font-size: 0.95rem;
  font-weight: 600;
  transition: all 0.2s ease;

  ${p => p.variant === 'primary' && css`
    background: ${p.theme.colors.primary};
    color: #fff;
    box-shadow: 0 4px 16px ${p.theme.colors.primary}55;
    &:hover { transform: translateY(-2px); box-shadow: 0 6px 24px ${p.theme.colors.primary}66; }
    &:active { transform: scale(0.98); }
  `}

  ${p => p.variant === 'secondary' && css`
    background: transparent;
    color: ${p.theme.colors.textSecondary};
    &:hover { color: ${p.theme.colors.textPrimary}; }
  `}

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }

  svg { font-size: 1.1rem; }

  @media (max-width: 768px) {
    padding: 16px 24px;
    font-size: 1rem;
    max-width: 100%;
    border-radius: 14px;
  }
`;

export const OnboardingSkip = styled.button`
  background: none;
  border: none;
  color: ${p => p.theme.colors.textSecondary};
  font-size: 0.8rem;
  cursor: pointer;
  margin-top: 16px;
  padding: 12px 24px;
  min-height: 44px;
  transition: color 0.2s;
  -webkit-tap-highlight-color: transparent;

  &:hover { color: ${p => p.theme.colors.textPrimary}; }
`;

export const OnboardingDots = styled.div`
  display: flex;
  gap: 8px;
  margin-bottom: 36px;

  @media (max-width: 768px) {
    margin-bottom: 28px;
  }
`;

export const OnboardingDot = styled.div`
  width: ${p => p.active ? '24px' : '8px'};
  height: 8px;
  border-radius: 4px;
  background: ${p => p.active ? p.theme.colors.primary : p.theme.colors.inputGray + '66'};
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
`;

export const OnboardingProgress = styled.div`
  width: 100%;
  max-width: 200px;
  height: 4px;
  border-radius: 2px;
  background: ${p => p.theme.colors.inputGray}33;
  margin-bottom: 28px;
  overflow: hidden;

  &::after {
    content: '';
    display: block;
    height: 100%;
    border-radius: 2px;
    background: linear-gradient(90deg, ${p => p.theme.colors.primary}, ${p => p.theme.colors.primary}88, ${p => p.theme.colors.primary});
    background-size: 200% 100%;
    animation: ${shimmer} 1.5s ease-in-out infinite;
    width: ${p => p.percent || 0}%;
    transition: width 0.5s ease;
  }

  @media (max-width: 768px) {
    max-width: 280px;
  }
`;

export const PermissionRow = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  width: 100%;
  max-width: 340px;
  padding: 16px;
  border-radius: 14px;
  background: ${p => p.theme.colors.surface};
  border: 1px solid ${p =>
    p.status === 'granted' ? p.theme.colors.success + '55'
    : p.status === 'denied' ? p.theme.colors.error + '44'
    : p.theme.colors.inputGray + '33'};
  margin-bottom: 10px;
  text-align: left;
  transition: all 0.3s ease;

  @media (max-width: 768px) {
    gap: 14px;
    padding: 16px;
    border-radius: 14px;
    max-width: 100%;
  }
`;

export const PermissionIcon = styled.div`
  width: 44px;
  height: 44px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  background: ${p => p.color || p.theme.colors.primary}18;

  svg {
    font-size: 1.2rem;
    color: ${p => p.color || p.theme.colors.primary};
  }

  @media (max-width: 768px) {
    width: 44px;
    height: 44px;
  }
`;

export const PermissionText = styled.div`
  flex: 1;
  min-width: 0;

  .title {
    font-size: 0.9rem;
    font-weight: 600;
    color: ${p => p.theme.colors.textPrimary};
    margin-bottom: 2px;
  }
  .desc {
    font-size: 0.75rem;
    color: ${p => p.theme.colors.textSecondary};
    line-height: 1.3;
  }

  @media (max-width: 768px) {
    .title { font-size: 0.95rem; }
    .desc { font-size: 0.82rem; line-height: 1.4; }
  }
`;

export const PermissionBadge = styled.div`
  width: 26px;
  height: 26px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  font-size: 0.7rem;

  ${p => p.status === 'granted' && css`
    background: ${p.theme.colors.success}22;
    color: ${p.theme.colors.success};
    animation: ${checkPop} 0.4s cubic-bezier(0.16, 1, 0.3, 1);
  `}
  ${p => p.status === 'denied' && css`
    background: ${p.theme.colors.error}22;
    color: ${p.theme.colors.error};
  `}
  ${p => p.status === 'pending' && css`
    background: ${p.theme.colors.inputGray}22;
    color: ${p.theme.colors.inputGray};
  `}

  @media (max-width: 768px) {
    width: 28px;
    height: 28px;
    font-size: 0.75rem;
  }
`;

export { pulse, fadeIn, slideUp, float, shimmer, scaleIn };
